import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DashboardInputLayout from '../components/DashboardInputLayout.jsx';
import DashboardFilterPanel from '../components/DashboardFilterPanel.jsx';
import DashboardInputToolbar from '../components/DashboardInputToolbar.jsx';
import DrilldownModal from '../components/DrilldownModal.jsx';
import ScheduleRecordEditor from '../components/ScheduleRecordEditor.jsx';
import { getScheduleChart, getProgressSchedule, getScheduleOemRegionPairs, getActors } from '../api/dashboardApi';
import { DASHBOARD_TABS } from '../constants/tabs';
import {
  parseDate,
  startOfDay,
  diffInDays,
  clamp,
  buildTimeline,
  statusToTone,
  buildBarStyle,
} from '../utils/scheduleListConverters.js';
import '../styles/dashboard-schedule-list.css';

const LEFT_COLUMN_WIDTH = 120;
const PERIOD_DAYS = { yearly: 365, half: 183, quarter: 91, month: 30 };
const HOURS_IN_DAY = 24;
// 初期表示で現在時刻線を置く位置（表示領域の左からの比率）
const NOW_LINE_VIEW_RATIO = 1 / 3;

// マイルストンのラベル配置（dashboard-schedule-list.css の .dsl-milestone と揃える）
const MILESTONE_FONT = "12px 'Noto Sans JP', sans-serif";
const MILESTONE_ROW_HEIGHT = 20;
const MILESTONE_ROW_PADDING = 2;
const MILESTONE_LABEL_GAP = 8;

// アクターの表示順序
const ACTOR_ORDER = [
  "日野様",
  "モビ推",
  "モビシス",
  "モビシス(SIM)",
  "開発WG①",
  "開発WG②",
  "情シスWG(シス企)",
  "情シスWG(GRABIS)",
  "運用WG",
  "PMO",
];

// canvas は生成コストが高いので使い回す。未計測なら null、非対応環境なら false。
let milestoneMeasureContext = null;

// ラベルの実幅が分からないと段の割り当てができない。DOM を測ると描画後に
// 段がずれてちらつくため、canvas で描画前に計算する。
function measureMilestoneLabel(text) {
  if (milestoneMeasureContext === null) {
    /* istanbul ignore next -- jsdom/ブラウザとも document は必ず存在する */
    const context = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    if (context) context.font = MILESTONE_FONT;
    milestoneMeasureContext = context || false;
  }

  const measured = milestoneMeasureContext ? milestoneMeasureContext.measureText(text).width : 0;
  if (measured > 0) return measured;

  // canvas が使えない環境（テスト等）では全角12px・半角7px で概算する
  let estimated = 0;
  for (const character of text) {
    estimated += character.charCodeAt(0) > 0xff ? 12 : 7;
  }
  return estimated;
}


export default function DashboardScheduleListPage() {
  const [mode, setMode] = useState('middle');
  const [period, setPeriod] = useState('yearly');
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dayWidth, setDayWidth] = useState(20);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewWidth, setViewWidth] = useState(0);
  const [oemRegionOptions, setOemRegionOptions] = useState([]);
  const [oemRegionLookup, setOemRegionLookup] = useState(new Map());
  const [oemRegionFilter, setOemRegionFilter] = useState('日野×日本');
  const [actorOptions, setActorOptions] = useState([]);
  const [actorFilter, setActorFilter] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [drilldownItem, setDrilldownItem] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [dependencyArrows, setDependencyArrows] = useState([]);
  const wrapRef = useRef(null);
  const bodyRef = useRef(null);
  const scrollRafRef = useRef(null);

  useEffect(() => {
    Promise.all([getScheduleOemRegionPairs(), getActors()]).then(([pairs, actorData]) => {
      const opts = [];
      const lookup = new Map();
      pairs.forEach((p) => {
        if (!lookup.has(p.label)) {
          opts.push(p.label);
          lookup.set(p.label, { oemId: p.oem_id, regionId: p.region_id });
        }
      });
      setOemRegionOptions(opts);
      setOemRegionLookup(lookup);
      setActorOptions(actorData);
    });
  }, []);

  const load = useCallback(async () => {
    // oemRegionLookup が未準備の場合はデータ取得を待機
    if (oemRegionFilter && oemRegionLookup.size === 0) return;
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (oemRegionFilter) {
        const ids = oemRegionLookup.get(oemRegionFilter);
        if (ids) {
          params.oem_id = ids.oemId;
          params.region_id = ids.regionId;
        }
      }
      if (actorFilter) params.actor_id = actorFilter;
      const data = await getScheduleChart(params);
      setChartData(data);
    } catch {
      setError('スケジュールデータの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [oemRegionFilter, oemRegionLookup, actorFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Actor中心のレーン構造
  const actorLanes = useMemo(() => {
    if (!chartData?.lanes) return [];

    return chartData.lanes.map((lane) => {
      const rows = [];
      for (const parentItem of lane.items) {
        if (mode === 'middle') {
          rows.push({
            id: parentItem.id,
            actor: lane.actor,
            label: parentItem.label,
            start: parseDate(parentItem.start),
            end: parseDate(parentItem.end),
            tone: statusToTone(parentItem.status),
            milestone: null,
            level: 'middle',
            children: parentItem.children,
          });
        } else {
          for (const child of parentItem.children) {
            rows.push({
              id: child.id,
              actor: lane.actor,
              label: child.label,
              start: parseDate(child.start),
              end: parseDate(child.end),
              tone: statusToTone(child.status),
              milestone: child.milestone,
              level: 'small',
              successorIds: child.successor_ids || [],
            });
          }
        }
      }

      rows.sort((a, b) => {
        if (!a.start && !b.start) return a.label.localeCompare(b.label, 'ja');
        if (!a.start) return 1;
        if (!b.start) return -1;
        return a.start.getTime() - b.start.getTime();
      });

      return { actor: lane.actor, rows };
    }).filter((lane) => lane.rows.length > 0).sort((a, b) => {
      const ai = ACTOR_ORDER.indexOf(a.actor);
      const bi = ACTOR_ORDER.indexOf(b.actor);
      return (ai === -1 ? ACTOR_ORDER.length : ai) - (bi === -1 ? ACTOR_ORDER.length : bi);
    });
  }, [chartData, mode]);

  const allRows = useMemo(
    () => actorLanes.flatMap((lane) => lane.rows),
    [actorLanes],
  );

  const timeline = useMemo(() => buildTimeline(allRows), [allRows]);

  // --- 期間トグルでズームレベルを動的に計算 ---
  /* istanbul ignore next -- period は期間トグルの3値のみで PERIOD_DAYS に必ず存在する */
  const spanDays = PERIOD_DAYS[period] || 365;

  useLayoutEffect(() => {
    const el = wrapRef.current;
    /* istanbul ignore if -- ref は初回描画後に必ず存在し、dayCount も1以上になる */
    if (!el || timeline.dayCount <= 0) return;

    const compute = () => {
      const availableWidth = Math.max(el.clientWidth - LEFT_COLUMN_WIDTH, 0);
      if (!availableWidth) return;
      // spanDays分が画面幅にぴったり収まるよう dayWidth を算出
      const needed = availableWidth / spanDays;
      setDayWidth(Math.max(needed, 2));
      setViewWidth(el.clientWidth);
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [spanDays, timeline.dayCount]);

  // スクロール位置をCSS変数に反映（バー内ラベル追従用）
  useEffect(() => {
    const el = wrapRef.current;
    /* istanbul ignore if -- ref は初回描画後に必ず存在する */
    if (!el) return;
    const onScroll = () => {
      el.style.setProperty('--scroll-x', `${el.scrollLeft}px`);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        setScrollLeft(el.scrollLeft);
        scrollRafRef.current = null;
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  // 期間変更時、現在時刻線が表示領域の左1/3に来る位置へ自動スクロール
  useLayoutEffect(() => {
    const el = wrapRef.current;
    /* istanbul ignore if -- 同上 */
    if (!el || timeline.dayCount <= 0) return;

    const now = new Date();
    const offsetDays = diffInDays(timeline.startDay, startOfDay(now)) + now.getHours() / HOURS_IN_DAY;
    const trackWidth = Math.max(el.clientWidth - LEFT_COLUMN_WIDTH, 0);
    const maxScroll = Math.max(el.scrollWidth - el.clientWidth, 0);
    // 期間の端に近いと1/3に置けないため、スクロール可能範囲に丸める
    el.scrollLeft = clamp(offsetDays * dayWidth - trackWidth * NOW_LINE_VIEW_RATIO, 0, maxScroll);
  }, [dayWidth, timeline.startDay, timeline.dayCount]);

  // 中項目バークリックで小項目ドリルダウンを表示
  const handleMiddleBarClick = useCallback((e, item) => {
    /* istanbul ignore if -- クリックできるのは子を持つ中項目バーのみ */
    if (item.level !== 'middle' || !item.children?.length) return;
    setDrilldownItem(item);
  }, []);

  // 小項目クリックでレコード詳細を表示
  const handleChildClick = useCallback(async (child) => {
    if (!child.id) return;
    try {
      const row = await getProgressSchedule(child.id);
      setEditingRow(row);
    } catch {
      // fetch failed - ignore
    }
  }, []);

  const handleEditorSaved = useCallback(() => {
    setEditingRow(null);
    setDrilldownItem(null);
    load();
  }, [load]);

  const milestoneRows = useMemo(() => {
    if (!chartData?.lanes) return [];
    const items = [];
    for (const lane of chartData.lanes) {
      for (const parentItem of lane.items) {
        for (const child of parentItem.children) {
          // 開始日がないものは配置できないのでここで除外する。
          // 件数を絞ると領域IDの若いレーンだけが残り、日付が入っていても
          // 表示されないマイルストンが出るため上限は設けない。
          if (!child.milestone || !child.start) continue;
          items.push({
            id: child.id,
            label: child.label,
            start: parseDate(child.start),
            end: parseDate(child.end),
            milestone: true,
          });
        }
      }
    }
    return items;
  }, [chartData]);

  // 日付が近いマイルストンはラベルが重なるため、左端から貪欲に段を割り当てる。
  // 先行するラベルの右端を越えていれば同じ段、越えていなければ次の段に置く。
  const milestoneLayout = useMemo(() => {
    const placed = [];
    const rowRightEdges = [];

    const positioned = milestoneRows
      .map((item) => ({ item, style: buildBarStyle(item, timeline.startDay, timeline.days.length, dayWidth) }))
      .filter(({ style }) => style.display !== 'none')
      .map(({ item, style }) => ({ item, left: parseFloat(style.left) }))
      .sort((a, b) => a.left - b.left);

    for (const { item, left } of positioned) {
      const right = left + measureMilestoneLabel(`▼ ${item.label}`) + MILESTONE_LABEL_GAP;
      let row = rowRightEdges.findIndex((edge) => edge <= left);
      /* istanbul ignore else -- 先頭要素は必ず新しい段になるため -1 になる */
      if (row === -1) row = rowRightEdges.length;
      rowRightEdges[row] = right;
      placed.push({ item, left, row });
    }

    return { items: placed, rowCount: Math.max(rowRightEdges.length, 1) };
  }, [milestoneRows, timeline, dayWidth]);



  // --- 依存関係矢印: DOM の実位置から座標計算（小項目モードのみ） ---
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (mode !== 'small' || !body || !actorLanes.length) {
      setDependencyArrows([]);
      return;
    }

    // successorIds を持つ行の id を収集
    const idsWithSuccessors = new Set();
    const allSuccessorTargets = new Set();
    for (const lane of actorLanes) {
      for (const row of lane.rows) {
        if (row.successorIds?.length) {
          idsWithSuccessors.add(row.id);
          for (const sid of row.successorIds) allSuccessorTargets.add(sid);
        }
      }
    }
    if (idsWithSuccessors.size === 0) { setDependencyArrows([]); return; }

    // DOM からバー要素の実位置を取得
    const bodyRect = body.getBoundingClientRect();
    const barEls = body.querySelectorAll('[data-schedule-id]');
    const barPositions = new Map();
    barEls.forEach((el) => {
      const id = Number(el.dataset.scheduleId);
      if (!idsWithSuccessors.has(id) && !allSuccessorTargets.has(id)) return;
      const rect = el.getBoundingClientRect();
      barPositions.set(id, {
        right: rect.right - bodyRect.left,
        left: rect.left - bodyRect.left,
        centerY: (rect.top + rect.bottom) / 2 - bodyRect.top,
      });
    });

    // 矢印パスを生成
    const arrows = [];
    for (const lane of actorLanes) {
      for (const row of lane.rows) {
        if (!row.successorIds?.length) continue;
        const from = barPositions.get(row.id);
        /* istanbul ignore if -- 依存元のバー位置は同じループで必ず登録済み */
        if (!from) continue;
        for (const sid of row.successorIds) {
          const to = barPositions.get(sid);
          if (!to) continue;
          const x1 = from.right;
          const y1 = from.centerY;
          const x2 = to.left;
          const y2 = to.centerY;
          const dx = Math.abs(x2 - x1) * 0.4;
          arrows.push({
            key: `${row.id}-${sid}`,
            d: `M ${x1},${y1} C ${x1 + dx},${y1} ${x2 - dx},${y2} ${x2},${y2}`,
          });
        }
      }
    }
    setDependencyArrows(arrows);
  }, [actorLanes, timeline, dayWidth, mode]);

  const nowLineLeft = useMemo(() => {
    const now = new Date();
    const dayOffset = diffInDays(timeline.startDay, startOfDay(now));
    if (dayOffset < 0 || dayOffset > timeline.days.length - 1) return null;
    // 当日の時刻比率で時間単位の位置を算出
    const hourFraction = now.getHours() / HOURS_IN_DAY;
    return (dayOffset + hourFraction) * dayWidth + LEFT_COLUMN_WIDTH;
  }, [timeline, dayWidth]);

  const tableWidth = LEFT_COLUMN_WIDTH + timeline.days.length * dayWidth;

  const scrollToBar = useCallback((barLeftPx) => {
    wrapRef.current?.scrollTo({ left: Math.max(0, barLeftPx - 50), behavior: 'smooth' });
  }, []);

  const currentDayIndex = useMemo(() => {
    /* istanbul ignore if -- buildTimeline は必ず1日以上を返す */
    if (!timeline.days.length) return -1;
    return Math.min(
      Math.floor(scrollLeft / dayWidth),
      timeline.days.length - 1,
    );
  }, [scrollLeft, dayWidth, timeline.days.length]);

  /* istanbul ignore next -- 同上。currentDayIndex は必ず0以上になる */
  const currentVisibleDay = currentDayIndex >= 0 ? timeline.days[currentDayIndex] : null;

  const effectiveViewWidth = viewWidth - LEFT_COLUMN_WIDTH;

  return (
    <DashboardInputLayout
      inputTabIndex={2}
      primaryTabActiveIndex={0}
      secondaryTabs={DASHBOARD_TABS}
      mainClassName="dsl-main"
      rootClassName="dsl-page"
      toolbar={
        <DashboardInputToolbar
          showFilterToggle
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters((prev) => !prev)}
        />
      }
      filters={
        showFilters ? (
          <DashboardFilterPanel
            fields={[
              {
                key: 'oemRegion',
                label: '対地OEM',
                type: 'select',
                options: [
                  { value: '', label: 'すべて' },
                  ...oemRegionOptions.map((label) => ({ value: label, label })),
                ],
              },
              {
                key: 'actor_id',
                label: '領域',
                type: 'select',
                options: [
                  { value: '', label: 'すべて' },
                  ...actorOptions.map((a) => ({ value: String(a.id), label: a.actor })),
                ],
              },
            ]}
            values={{ oemRegion: oemRegionFilter, actor_id: actorFilter }}
            onFieldChange={(key, value) => {
              if (key === 'oemRegion') setOemRegionFilter(value);
              if (key === 'actor_id') setActorFilter(value);
            }}
            onClear={() => { setOemRegionFilter(''); setActorFilter(''); }}
          />
        ) : null
      }
    >
      <section className="dsl-toolbar">
        <div className="dsl-legend">
          <div className="dsl-legend-box dsl-legend-box--done">完了</div>
          <div className="dsl-legend-box dsl-legend-box--progress">進行中</div>
          <div className="dsl-legend-box dsl-legend-box--delay">遅延</div>
          <div className="dsl-legend-box dsl-legend-box--info">未着手</div>
        </div>

        <div className="dsl-toolbar-right">
          <div className="dsl-toggle-group">
            <button className={`dsl-toggle ${mode === 'middle' ? 'is-active' : ''}`} onClick={() => setMode('middle')} type="button">中項目</button>
            <button className={`dsl-toggle ${mode === 'small' ? 'is-active' : ''}`} onClick={() => setMode('small')} type="button">小項目</button>
          </div>

          <div className="dsl-divider" />

          <div className="dsl-toggle-group">
            <button className={`dsl-toggle ${period === 'yearly' ? 'is-active' : ''}`} onClick={() => setPeriod('yearly')} type="button">年次</button>
            <button className={`dsl-toggle ${period === 'half' ? 'is-active' : ''}`} onClick={() => setPeriod('half')} type="button">半年</button>
            <button className={`dsl-toggle ${period === 'quarter' ? 'is-active' : ''}`} onClick={() => setPeriod('quarter')} type="button">四半期</button>
            <button className={`dsl-toggle ${period === 'month' ? 'is-active' : ''}`} onClick={() => setPeriod('month')} type="button">1ヶ月</button>
          </div>
        </div>
      </section>

      <section className="dsl-panel">
        <div className="dsl-table-wrap" ref={wrapRef}>
          <div className="dsl-table" style={{ width: tableWidth }}>
            <div className="dsl-header-years">
              <div className="dsl-header-left dsl-header-left--year">
                {/* istanbul ignore next -- 同上 */ currentVisibleDay ? `${currentVisibleDay.year}年` : ''}
              </div>
              {timeline.yearSegments.map((segment, index) => {
                const cellLeft = LEFT_COLUMN_WIDTH + timeline.yearSegments.slice(0, index).reduce((acc, s) => acc + s.count * dayWidth, 0);
                return (
                  <div className={`dsl-year-cell ${index === 0 ? 'is-first' : ''}`} key={segment.year} style={{ width: segment.count * dayWidth, '--cell-left': `${cellLeft}px` }}>
                    <span className="dsl-year-text">{segment.year}年</span>
                  </div>
                );
              })}
            </div>

            <div className="dsl-header-months">
              <div className="dsl-header-left dsl-header-left--month">
                {/* istanbul ignore next -- 同上 */ currentVisibleDay ? `${currentVisibleDay.month}月` : ''}
              </div>
              {timeline.monthSegments.map((segment, index) => {
                const cellLeft = LEFT_COLUMN_WIDTH + timeline.monthSegments.slice(0, index).reduce((acc, s) => acc + s.count * dayWidth, 0);
                return (
                  <div className="dsl-month-cell" key={`${segment.year}-${segment.month}`} style={{ width: segment.count * dayWidth, '--cell-left': `${cellLeft}px` }}>
                    <span className="dsl-month-text">{segment.month}月</span>
                  </div>
                );
              })}
            </div>

            <div className="dsl-body" ref={bodyRef}>
              <div className="dsl-row">
                <div className="dsl-label-cell">マイルストン</div>
                <div
                  className="dsl-track-cell"
                  style={{
                    width: timeline.days.length * dayWidth,
                    height: milestoneLayout.rowCount * MILESTONE_ROW_HEIGHT + MILESTONE_ROW_PADDING * 2,
                  }}
                >
                  {milestoneLayout.items.map(({ item, left, row }, index) => (
                    <span
                      className="dsl-milestone"
                      key={`${item.id}-mile-${index}`}
                      style={{ left: `${left}px`, top: `${row * MILESTONE_ROW_HEIGHT + MILESTONE_ROW_PADDING}px` }}
                    >
                      ▼ {item.label}
                    </span>
                  ))}
                </div>
              </div>

              {loading && (
                <div className="dsl-message-row">読み込み中...</div>
              )}

              {!loading && error && (
                <div className="dsl-message-row">{error}</div>
              )}

              {!loading && !error && actorLanes.length === 0 && (
                <div className="dsl-message-row">データがありません</div>
              )}

              {!loading && !error && actorLanes.map((lane) => (
                <div className="dsl-group" key={lane.actor}>
                  <div className="dsl-group-col" style={{ height: `${lane.rows.length * 40}px` }}>
                    <div className="dsl-group-label">{lane.actor}</div>
                  </div>
                  <div className="dsl-group-rows">
                    {lane.rows.map((item, index) => {
                      const barStyle = buildBarStyle(item, timeline.startDay, timeline.days.length, dayWidth);
                      const barLeft = parseFloat(barStyle.left) || 0;
                      const barW = parseFloat(barStyle.width) || 0;
                      const hasBar = barStyle.display !== 'none';
                      const isOffLeft = hasBar && (barLeft + barW) < scrollLeft;
                      const isOffRight = hasBar && effectiveViewWidth > 0 && barLeft > (scrollLeft + effectiveViewWidth);
                      return (
                        <div className="dsl-row" key={`${item.id}-${index}`}>
                          <div className="dsl-track-cell" style={{ width: timeline.days.length * dayWidth }}>
                            <div
                              className={`dsl-bar dsl-bar--${item.tone} dsl-bar--clickable`}
                              data-schedule-id={item.id}
                              style={{ ...barStyle, '--bar-left': `${barLeft}px` }}
                              onClick={
                                item.level === 'middle' && item.children?.length
                                  ? (e) => handleMiddleBarClick(e, item)
                                  : item.level === 'small'
                                    ? () => handleChildClick(item)
                                    : undefined
                              }
                            >
                              <span className="dsl-bar-label">{item.label}</span>
                            </div>
                            {isOffLeft && (
                              <button className="dsl-offscreen dsl-offscreen--left" style={{ left: `${scrollLeft}px` }}
                                onClick={() => scrollToBar(barLeft)} type="button">
                                ◀ {item.label}
                              </button>
                            )}
                            {isOffRight && (
                              <button className="dsl-offscreen dsl-offscreen--right" style={{ left: `${scrollLeft + effectiveViewWidth}px` }}
                                onClick={() => scrollToBar(barLeft)} type="button">
                                {item.label} ▶
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {dependencyArrows.length > 0 && (
                <svg
                  className="dsl-dependency-arrows"
                  width={tableWidth}
                  height={/* istanbul ignore next -- 矢印を描く時点で本体の ref は必ず存在する */ bodyRef.current ? bodyRef.current.scrollHeight : 0}
                >
                  <defs>
                    <marker id="dsl-arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                      <path d="M 0,0 L 8,3 L 0,6 Z" fill="#374151" />
                    </marker>
                  </defs>
                  {dependencyArrows.map((arrow) => (
                    <path
                      key={arrow.key}
                      className="dsl-dependency-path"
                      d={arrow.d}
                      markerEnd="url(#dsl-arrowhead)"
                    />
                  ))}
                </svg>
              )}
            </div>

            {/* 線は2px幅なので、左の固定列に入ったかどうかは全か無かで判定できる */}
            {nowLineLeft !== null && nowLineLeft >= scrollLeft + LEFT_COLUMN_WIDTH && (
              <div className="dsl-now-line" style={{ left: `${nowLineLeft}px` }} />
            )}

          </div>
        </div>
      </section>

      {drilldownItem && (
        <DrilldownModal
          title={`${drilldownItem.label} - 小項目一覧`}
          onClose={() => setDrilldownItem(null)}
        >
          {/* istanbul ignore next -- ドリルダウンは子を持つ中項目からしか開かない */ drilldownItem.children.length === 0 ? (
            <div className="dashboard-drilldown-empty">小項目データがありません</div>
          ) : (
            drilldownItem.children.map((child, idx) => (
              <div
                className="dashboard-drilldown-row"
                key={child.id || idx}
                onClick={() => handleChildClick(child)}
              >
                <span className="dashboard-drilldown-row-name">{child.small_category || child.label}</span>
                <div className="dashboard-drilldown-row-right">
                  <span>{child.actor || ''}</span>
                </div>
              </div>
            ))
          )}
        </DrilldownModal>
      )}

      <ScheduleRecordEditor
        open={Boolean(editingRow)}
        initialRow={editingRow}
        onClose={() => setEditingRow(null)}
        onDeleted={handleEditorSaved}
        onSaved={handleEditorSaved}
      />
    </DashboardInputLayout>
  );
}
